import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Loader2, Eye, EyeOff } from "lucide-react";
import LogoAnimation from "@/components/LogoAnimation";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

// v7.05 — signup restyled to match the v7.04 login (admoji.com/ads look).
// Same tokens (from ./auth-theme), same card/heading/field treatment; only
// the copy, the third field and the trial line differ. LogoAnimation is kept
// (its ink is the same near-black) so the entrance animation lives on.
import {
  PAGE, CARD, INK, MUTED, LINE, FIELD, BTN, BTN_HOVER, PINK, SANS, SERIF, MONO,
} from "./auth-theme";

export default function Signup({ onLogin, variant = "standard" }: { onLogin: () => Promise<void> | void; variant?: "standard" | "promo" }) {
  // v7.46 — /promosignup: same form, but the coupon is open + REQUIRED and
  // there is no trial/card step afterwards (credits are granted on the spot).
  const isPromo = variant === "promo";
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // v7.28 — promo coupon: hidden behind a link so the field isn't in the way
  // for normal signups. Checked live so a promo user learns a bad code here,
  // not after creating an account.
  const [couponOpen, setCouponOpen] = useState(variant === "promo");
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState<{ status: "idle" | "checking" | "ok" | "bad"; message?: string; credits?: number }>({ status: "idle" });
  const [, setLocation] = useLocation();
  const submittingRef = useRef(false);

  const checkCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponState({ status: "idle" }); return; }
    setCouponState({ status: "checking" });
    try {
      const res = await fetch("/api/auth/validate-coupon", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data?.valid) {
        setCouponState({ status: "ok", credits: data.credits });
      } else {
        setCouponState({ status: "bad", message: data?.message || "That coupon code isn't valid." });
      }
    } catch {
      setCouponState({ status: "bad", message: "Couldn't check that code — try again." });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError("");

    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (isPromo && !couponCode.trim()) {
      setError("A promo code is required to sign up on this page.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(), password, name: name.trim(),
          // Send whatever they typed — the server re-validates and a bad
          // code never fails signup. Gating on couponState would silently
          // drop a valid code when the async check hadn't resolved yet.
          ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
          ...(isPromo ? { promoSignup: true } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || "Signup failed");
        return;
      }

      // Account created + session set. Wait for the auth-context refresh
      // to land BEFORE navigating, otherwise the paywall route sees `user`
      // as still null and bounces us back to /login.
      // v7.28 — a coupon can validate at type-time and still fail at register
      // (deactivated, last slot taken). Say so rather than letting them find
      // out by never seeing the credits.
      const created = await res.json().catch(() => null);
      if (couponCode.trim() && created?.coupon && created.coupon.applied === false) {
        setError("Your account was created, but that coupon couldn't be applied — contact support before starting your trial.");
        setLoading(false);
        submittingRef.current = false;
        await onLogin();
        return;
      }

      await onLogin();
      // Promo users already hold credits and a subscription row, so the plan
      // picker would just bounce them — drop them straight into the app.
      setLocation(isPromo ? "/" : "/billing/select-plan");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const fieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = PINK;
    e.target.style.boxShadow = "0 0 0 3px rgba(169,49,91,0.15)";
    e.target.style.backgroundColor = CARD;
  };
  const fieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = LINE;
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = FIELD;
  };

  const fieldStyle: React.CSSProperties = {
    backgroundColor: FIELD,
    border: `1px solid ${LINE}`,
    borderRadius: 9,
    color: INK,
    fontSize: 14,
    fontFamily: SANS,
  };
  const labelStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 500, color: MUTED };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ backgroundColor: PAGE, fontFamily: SANS }}
      data-testid="signup-page"
    >
      <div className="w-full" style={{ maxWidth: 400 }}>
        {/* Logo above the card, mirroring login and the marketing header */}
        <div className="flex justify-center mb-7" data-testid="text-logo">
          <LogoAnimation size={126} />
        </div>

        <div
          style={{
            backgroundColor: CARD,
            border: `1px solid ${LINE}`,
            borderRadius: 14,
            padding: "34px 32px 30px",
          }}
        >
          <div className="mb-6">
            <div
              style={{
                fontFamily: MONO,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 10,
              }}
            >
              Ad Manager
            </div>
            <h1
              style={{
                fontFamily: SANS,
                fontSize: 34,
                fontWeight: 600,
                letterSpacing: "-0.032em",
                lineHeight: 1.08,
                color: INK,
                margin: 0,
              }}
            >
              {isPromo ? "Redeem your" : "Create your"}{" "}
              <span style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, fontSize: 37 }}>
                {isPromo ? "invite." : "account."}
              </span>
            </h1>
            <p
              className="font-medium"
              style={{ fontSize: 13, color: PINK, marginTop: 9 }}
              data-testid="signup-trial-line"
            >
              {isPromo
                ? "Enter your promo code — no card needed"
                : "Try free for 7 days — 3,000 free credits"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                className="text-sm px-4 py-3"
                style={{
                  backgroundColor: "rgba(169,49,91,0.07)",
                  color: "#8F2748",
                  border: "1px solid rgba(169,49,91,0.32)",
                  borderRadius: 9,
                }}
                data-testid="text-signup-error"
              >
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block mb-1.5" style={labelStyle}>
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                autoFocus
                data-testid="input-name"
                className="w-full h-11 px-3 outline-none transition-all"
                style={fieldStyle}
                onFocus={fieldFocus}
                onBlur={fieldBlur}
              />
            </div>

            <div>
              <label htmlFor="email" className="block mb-1.5" style={labelStyle}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                data-testid="input-email"
                className="w-full h-11 px-3 outline-none transition-all"
                style={fieldStyle}
                onFocus={fieldFocus}
                onBlur={fieldBlur}
              />
            </div>

            <div>
              <label htmlFor="password" className="block mb-1.5" style={labelStyle}>
                Password
              </label>
              <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                data-testid="input-password"
                className="w-full h-11 pl-3 pr-12 outline-none transition-all"
                style={fieldStyle}
                onFocus={fieldFocus}
                onBlur={fieldBlur}
              />
              <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onMouseDown={event => event.preventDefault()} onClick={() => setShowPassword(value => !value)} className="absolute right-1 top-1 flex h-9 w-10 items-center justify-center rounded focus-visible:outline focus-visible:outline-2" style={{color: MUTED}} data-testid="signup-password-toggle">{showPassword ? <EyeOff size={18} aria-hidden="true"/> : <Eye size={18} aria-hidden="true"/>}</button>
              </div>
            </div>

            {/* v7.28 — promo coupon, collapsed by default (Dan: "a little
                coupon button that opens a new input field so it's not there
                open by default"). */}
            {!couponOpen ? (
              <button
                type="button"
                data-testid="signup-coupon-toggle"
                onClick={() => setCouponOpen(true)}
                style={{ fontSize: 12.5, fontWeight: 600, color: PINK, background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                Have a coupon?
              </button>
            ) : (
              <div>
                <label htmlFor="coupon" className="block mb-1.5" style={labelStyle}>
                  {isPromo ? "Promo code (required)" : "Coupon code"}
                </label>
                <div className="flex gap-2">
                  <input
                    id="coupon"
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState({ status: "idle" }); }}
                    onBlur={checkCoupon}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void checkCoupon(); } }}
                    placeholder="PROMO250"
                    autoCapitalize="characters"
                    spellCheck={false}
                    data-testid="input-coupon"
                    className="flex-1 h-11 px-3 outline-none transition-all"
                    style={{ ...fieldStyle, letterSpacing: "0.06em", fontWeight: 600 }}
                    onFocus={fieldFocus}
                  />
                  <button
                    type="button"
                    data-testid="signup-coupon-apply"
                    onClick={() => void checkCoupon()}
                    disabled={!couponCode.trim() || couponState.status === "checking"}
                    style={{ height: 44, padding: "0 14px", borderRadius: 9, border: `1px solid ${LINE}`, background: CARD, color: INK, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    {couponState.status === "checking" ? "…" : "Apply"}
                  </button>
                </div>
                {couponState.status === "ok" && (
                  <div data-testid="coupon-ok" style={{ fontSize: 11.5, color: "#2F7A55", marginTop: 6, fontWeight: 500 }}>
                    Coupon applied — {couponState.credits?.toLocaleString()} free credits land when your trial starts.
                  </div>
                )}
                {couponState.status === "bad" && (
                  <div data-testid="coupon-bad" style={{ fontSize: 11.5, color: "#8F2748", marginTop: 6 }}>
                    {couponState.message}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="button-signup"
              className="w-full flex items-center justify-center transition-colors disabled:opacity-60"
              style={{
                height: 46,
                backgroundColor: BTN,
                color: "#FFFFFF",
                border: "1px solid transparent",
                borderRadius: 11,
                fontSize: 14.5,
                fontWeight: 600,
                fontFamily: SANS,
              }}
              onMouseEnter={(e) => {
                if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BTN_HOVER;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = BTN;
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create account"
              )}
            </button>

            {/* v7.46 — Google is hidden on the promo page: that route creates
                users through /api/auth/google, which has no required-coupon or
                card-free provisioning path. Email + password only here. */}
            {!isPromo && (<>
            <div className="flex items-center gap-3 pt-1">
              <div className="h-px flex-1" style={{ backgroundColor: LINE }} />
              <span style={{ fontSize: 11, color: MUTED }}>or</span>
              <div className="h-px flex-1" style={{ backgroundColor: LINE }} />
            </div>
            <GoogleSignInButton
              theme="outline"
              // v7.28 — carry the coupon through the Google path too.
              extraBody={couponCode.trim() ? { couponCode: couponCode.trim() } : undefined}
              onSuccess={async () => { await onLogin(); setLocation("/billing/select-plan"); }}
              onError={setError}
            />
            </>)}

            <p className="text-center" style={{ fontSize: 12.5, color: MUTED }}>
              Already have an account?{" "}
              <Link href="/login" style={{ fontWeight: 600, color: PINK }}>
                Sign in
              </Link>
            </p>

            <p className="text-center" style={{ fontSize: 11, color: MUTED, lineHeight: 1.5 }}>
              By creating an account you agree to our{" "}
              <a href="/terms" className="underline" style={{ color: MUTED }}>Terms of Service</a>{" "}
              and{" "}
              <a href="/privacy" className="underline" style={{ color: MUTED }}>Privacy Policy</a>.
            </p>
          </form>
        </div>

        <p className="text-center mt-6" style={{ fontSize: 11.5, color: MUTED }}>
          Admoji Ad Manager
        </p>
      </div>
    </div>
  );
}
